import { CUSTOMER_ACTIONS } from '@dadohr/shared/domainTypes/internalAlerts';
import styled from '@emotion/styled';
import {
  Checkbox,
  FormControlLabel,
  Link,
  Typography
} from '@material-ui/core';
import { AlertCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useGlobalConfig } from '../../../../admin/features/GlobalConfig';
import { ButtonWithPromise } from '../../../../components/ButtonWithPromise';
import { NoAccess } from '../../../../components/NoAccess';
import { PdfViewer } from '../../../../components/PdfViewer';
import { SwitchWithLabel } from '../../../../components/SwitchWithLabel';
import { useRouter } from '../../../../hooks/useRouter';
import { useTranslation } from '../../../../i18next';
import { GridHalfSized } from '../../../../layout/Grid';
import { PageBody } from '../../../../layout/PageBody';
import { PageToolbar, PageToolbarTitle } from '../../../../layout/PageToolbar';
import { Section } from '../../../../layout/Section';
import { useRoutes } from '../../../../routes';
import { useCurrentUser } from '../../../../services/currentUser';
import { useFeatureEnabled } from '../../../../services/features';
import { tryReportCustomerAction } from '../../../../services/internalAlerts';
import {
  acceptTos,
  setAIEnabled,
  useAITermsVersion,
  useOrgSettings
} from '../../../../services/orgSettings';
import { hasPermissionToAccessPage } from '../../../../services/permissions';
import {
  formatDatePrecise
} from '../../../../services/time';
import { useOrgSideNav } from '../sideNav';

const WarningBox = styled('div')((p) => ({
  backgroundColor: '#FEFAEC',
  color: '#000000',
  padding: p.theme.spacing(2),
  borderRadius: p.theme.custom.border.radius(),
  display: 'inline-flex',
  alignItems: 'flex-start',
  gap: p.theme.spacing(2)
}));

const WarningIcon = styled('div')({
  flexShrink: 0,
  color: '#000000',
  marginTop: 2
});


const CheckboxBox = styled('div')((p) => ({
  backgroundColor: '#FAFAFA',
  padding: p.theme.spacing(2),
  borderRadius: p.theme.custom.border.radius(),
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: p.theme.spacing(2)
}));

const ContentOrgAIFeatures = ({ orgId }: { orgId: string }) => {
  const { t } = useTranslation();
  const sideNav = useOrgSideNav();
  const currentUser = useCurrentUser();
  const { location } = useRouter();

  const inviteAdminLink = useMemo(() => {
    const params = new URLSearchParams(location.search);
    params.set('invite', 'true');
    return `/org/admins?${params.toString()}`;
  }, [location.search]);
  
  const [globalConfig] = useGlobalConfig();
  const currentTermsVersionId = globalConfig?.ai?.currentTermsVersion ?? '1.0';
  const [termsVersion] = useAITermsVersion(currentTermsVersionId);
  const [orgSettings] = useOrgSettings(orgId);
  const serverEnabled = orgSettings?.data?.ai?.enabled ?? false;
  const tosAcceptedVersion = orgSettings?.data?.ai?.tosAcceptedVersion;
  const tosAcceptedAt = orgSettings?.data?.ai?.tosAcceptedAt;
  const tosAcceptedByName = orgSettings?.data?.ai?.tosAcceptedByName;
  const isTosAccepted =
    !!currentTermsVersionId && tosAcceptedVersion === currentTermsVersionId;
  // When AI terms are accepted via the customer's contract, the in-app ToS gate
  // is bypassed entirely: no warning, no read-&-accept flow, no accepted-terms
  // details, and AI can be toggled without in-app acceptance.
  const acceptedAiTermsInContract = useFeatureEnabled(
    'ACCEPTED_AI_TERMS_IN_CONTRACT'
  );
  // ToS is satisfied either by in-app acceptance or via the signed contract.
  const tosSatisfied = isTosAccepted || acceptedAiTermsInContract;
  // Fetch the accepted version's terms (for PDF download) - may differ from current version
  const [acceptedTermsVersion] = useAITermsVersion(tosAcceptedVersion ?? currentTermsVersionId);
  const [optimisticEnabled, setOptimisticEnabled] = useState<boolean | null>(
    null
  );
  const isEnabled = optimisticEnabled ?? serverEnabled;

  const [authorizedChecked, setAuthorizedChecked] = useState(false);
  const [acceptTermsChecked, setAcceptTermsChecked] = useState(false);
  const canAcceptTos =
    authorizedChecked && acceptTermsChecked && !!currentTermsVersionId;

  const handleToggle = async () => {
    if (!tosSatisfied) return;
    const newValue = !isEnabled;
    setOptimisticEnabled(newValue);
    try {
      await setAIEnabled(orgId, newValue);
      tryReportCustomerAction({
        action: CUSTOMER_ACTIONS.AI_FEATURES_TOGGLED,
        orgId,
        adminUserId: currentUser.id,
        adminUserName: currentUser.firstName + ' ' + currentUser.lastName,
        enabled: newValue
      });
    } catch {
      // Revert on error
      setOptimisticEnabled(null);
    }
  };

  const handleAcceptTos = async () => {
    if (!canAcceptTos || !currentTermsVersionId) return;
    await acceptTos(orgId, 'ai', currentTermsVersionId);
    tryReportCustomerAction({
      action: CUSTOMER_ACTIONS.AI_TOS_ACCEPTED,
      orgId,
      adminUserId: currentUser.id,
      adminUserName: currentUser.firstName + ' ' + currentUser.lastName,
      tosVersion: currentTermsVersionId
    });
  };

  return (
    <PageBody noTopPadding sideNav={sideNav}>
      <PageToolbar>
        <PageToolbarTitle>
          <Typography component="h1" variant="h1">
            {t('pageTitle.orgAIFeatures')}
          </Typography>
        </PageToolbarTitle>
      </PageToolbar>

      <GridHalfSized>
        <div>
          <Section spacing={2}>
            <Typography variant="body1">
              {t('org.aiFeatures.description')}
            </Typography>
          </Section>

          <Section spacing={2}>
            <SwitchWithLabel
              checked={isEnabled}
              onChange={handleToggle}
              disabled={!tosSatisfied}
              label={
                <span>
                  {t('org.aiFeatures.toggleStatus.prefix')}{' '}
                  <strong>{isEnabled ? t('org.aiFeatures.toggleStatus.on') : t('org.aiFeatures.toggleStatus.off')}</strong>{' '}
                  {t('org.aiFeatures.toggleStatus.suffix')}
                </span>
              }
            />
          </Section>

          {!tosSatisfied && (
            <Section spacing={4}>
              <WarningBox>
                <WarningIcon>
                  <AlertCircle size={20} />
                </WarningIcon>
                <div>
                  <Typography variant="body2">{t('org.aiFeatures.tosWarning.p1')}</Typography>
                  <Typography variant="body2">{t('org.aiFeatures.tosWarning.p2')}</Typography>
                  <Typography variant="body2">
                    {t('org.aiFeatures.tosWarning.p3')}{' '}
                    <Link component={RouterLink} to={inviteAdminLink} style={{ fontWeight: 500 }}>
                      {t('org.aiFeatures.tosWarning.p3Link')}
                    </Link>
                    .
                  </Typography>
                </div>
              </WarningBox>
            </Section>
          )}

          {!tosSatisfied && currentTermsVersionId && (
            <>
              <Section spacing={2}>
                <Typography variant="h4">
                  {t('org.aiFeatures.tosHeading')}
                </Typography>
              </Section>

              {termsVersion?.data?.pdfUrl && (
                <Section spacing={2}>
                  <PdfViewer
                    url={termsVersion.data.pdfUrl}
                    height={800}
                    width="80%"
                    showPageCount
                    showDownload
                    downloadFilename="AI-Terms-of-Service.pdf"
                  />
                </Section>
              )}

              <Section spacing={4}>
                <CheckboxBox>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={authorizedChecked}
                        onChange={(e) => setAuthorizedChecked(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={t('org.aiFeatures.checkboxAuthorized', {
                      orgName: currentUser.org.name
                    })}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={acceptTermsChecked}
                        onChange={(e) => setAcceptTermsChecked(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={t('org.aiFeatures.checkboxAcceptTerms')}
                  />
                  <ButtonWithPromise
                    variant="contained"
                    color="primary"
                    onClick={handleAcceptTos}
                    disabled={!canAcceptTos}
                    pending={t('Applying')}
                  >
                    {t('org.aiFeatures.acceptTos')}
                  </ButtonWithPromise>
                </CheckboxBox>
              </Section>
            </>
          )}

          {isTosAccepted && tosAcceptedAt && !acceptedAiTermsInContract && (
            <Section spacing={4}>
              <Typography variant="body2">
                {t('org.aiFeatures.termsAccepted', {
                  date: formatDatePrecise(tosAcceptedAt, 'MMMM D, YYYY'),
                  name: tosAcceptedByName
                })}
              </Typography>
              {acceptedTermsVersion?.data?.pdfUrl && (
                <Link
                  href={acceptedTermsVersion.data.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  style={{ display: 'block', marginTop: 16, fontWeight: 500 }}
                >
                  {t('org.aiFeatures.downloadTerms')}
                </Link>
              )}
            </Section>
          )}
        </div>
      </GridHalfSized>
    </PageBody>
  );
};

export const PageOrgAIFeatures = () => {
  const currentUser = useCurrentUser();
  const { ROUTES } = useRoutes();
  const { t } = useTranslation();
  const sideNav = useOrgSideNav();

  if (!hasPermissionToAccessPage(currentUser, ROUTES.org.aiFeatures)) {
    return <NoAccess />;
  }

  return <ContentOrgAIFeatures orgId={currentUser.org.id} />;
};
